import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { motion, Variants } from "framer-motion";

import { useFilters } from "../../../hooks/useFilters";
import { useAppState } from "../../../contexts/AppContext";
import { useTransaction } from "../../../contexts/TransactionContext";
import {
  useGetWallets,
  useCreateWallet,
  walletsResourcePath,
} from "../../../services/queries/wallets";
import {
  useGetTransactions,
  transactionsResourcePath,
} from "../../../services/queries/transactions";
import {
  CreatableWallet,
  getMissingCreatableWallets,
} from "../../../utils/creatableWallets";
import { showToast } from "../../../utils/toast";
import { routePaths } from "../../../constants";
import WalletDepositDrawer from "../../../components/wallets/WalletDepositDrawer";
import DashboardGreeting from "../../../components/dashboard/DashboardGreeting";
import BalanceHero from "../../../components/dashboard/BalanceHero";
import WalletRail from "../../../components/dashboard/WalletRail";
import WalletSetup from "../../../components/dashboard/WalletSetup";
import RecentActivity from "../../../components/dashboard/RecentActivity";

export const Route = createFileRoute("/_app/dashboard/")({
  component: Dashboard,
});

const containerVariants: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 260, damping: 20 },
  },
};

function Dashboard() {
  const navigate = useNavigate();
  const { updateTransaction } = useTransaction();
  const {
    state: { user, business },
  } = useAppState();
  const businessId = business?.id || "";

  const { buildApiUrl } = useFilters();
  const { buildApiUrl: buildRecentUrl } = useFilters({ pageSize: 5 });

  // Wallets
  const {
    data: walletsData,
    isLoading: isLoadingWallets,
    refetch: refetchWallets,
  } = useGetWallets(buildApiUrl(walletsResourcePath));
  const wallets = walletsData?.data?.list || [];

  // Featured wallet = the sendable (NGN) wallet, else the first wallet
  const featuredWallet = wallets.find((w) => w.canWithdraw) ?? wallets[0];
  const otherWallets = wallets.filter((w) => w.id !== featuredWallet?.id);
  const missingWallets = getMissingCreatableWallets(wallets);

  // Recent transactions
  const { data: transactionsData, isLoading: isLoadingTransactions } =
    useGetTransactions(businessId, buildRecentUrl(transactionsResourcePath));
  const recentTransactions = transactionsData?.data?.list || [];

  // Deposit drawer
  const [isDepositOpen, setIsDepositOpen] = useState(false);
  const [depositCurrency, setDepositCurrency] = useState<string | null>(null);
  const [depositWalletId, setDepositWalletId] = useState<string | null>(null);

  const openDeposit = (walletId: string, currency: string) => {
    setDepositWalletId(walletId);
    setDepositCurrency(currency);
    setIsDepositOpen(true);
  };
  const closeDeposit = () => setIsDepositOpen(false);

  // Balance refresh (visual spin for a beat, then clear)
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = () => {
    setRefreshing(true);
    refetchWallets();
    setTimeout(() => setRefreshing(false), 800);
  };

  // Send money — pre-select the featured wallet, then go to the send flow
  const handleSend = () => {
    if (featuredWallet) {
      updateTransaction({ walletId: featuredWallet.id });
    }
    navigate({ to: routePaths.send });
  };

  // Create wallet
  const { mutate: createWalletMutate } = useCreateWallet();
  const [creatingCurrency, setCreatingCurrency] = useState<string | null>(null);

  const handleCreateWallet = (wallet: CreatableWallet) => {
    if (!businessId) {
      showToast("Missing business information", "error");
      return;
    }
    setCreatingCurrency(wallet.currency);
    createWalletMutate(
      {
        currencyCode: wallet.currency,
        currencySymbol: wallet.currencySymbol,
        canDeposit: wallet.canDeposit,
        canWithdraw: wallet.canWithdraw,
      },
      {
        onSuccess: () => {
          showToast(`${wallet.name} wallet created`, "success");
          setCreatingCurrency(null);
        },
        onError: () => {
          showToast(`Could not create ${wallet.name} wallet`, "error");
          setCreatingCurrency(null);
        },
      },
    );
  };

  const hasNoWallets = !isLoadingWallets && wallets.length === 0;

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="show"
      className="flex flex-col gap-6"
    >
      <motion.div variants={itemVariants}>
        <DashboardGreeting firstName={user?.firstName} />
      </motion.div>

      {hasNoWallets ? (
        <motion.div variants={itemVariants}>
          <WalletSetup
            wallets={missingWallets}
            creatingCurrency={creatingCurrency}
            onCreate={handleCreateWallet}
          />
        </motion.div>
      ) : (
        <>
          <motion.div variants={itemVariants}>
            <BalanceHero
              wallet={featuredWallet}
              loading={isLoadingWallets}
              refreshing={refreshing}
              onAdd={openDeposit}
              onSend={handleSend}
              onRefresh={handleRefresh}
            />
          </motion.div>

          {!isLoadingWallets && (
            <motion.div variants={itemVariants}>
              <WalletRail
                wallets={otherWallets}
                missing={missingWallets}
                creatingCurrency={creatingCurrency}
                onAdd={openDeposit}
                onCreate={handleCreateWallet}
              />
            </motion.div>
          )}

          {/* <motion.div variants={itemVariants}>
            <QuickActions
              onSend={handleSend}
              onAdd={() =>
                featuredWallet &&
                openDeposit(featuredWallet.id, featuredWallet.currency)
              }
            />
          </motion.div> */}

          <motion.div variants={itemVariants}>
            <RecentActivity
              transactions={recentTransactions}
              loading={isLoadingTransactions}
            />
          </motion.div>
        </>
      )}

      <WalletDepositDrawer
        isOpen={isDepositOpen}
        currency={depositCurrency}
        walletId={depositWalletId}
        onClose={closeDeposit}
        buildApiUrl={buildApiUrl}
      />
    </motion.div>
  );
}
